import { useEffect, useState } from "react";
import { Building2, TrendingUp, CircleDollarSign, Percent, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Loading } from "@/components/shared";

const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-900 rounded-xl border border-slate-800 p-5">
      <div className="flex items-start justify-between">
        <span className="text-sm text-slate-400">{label}</span>
        {Icon && <span className="p-2 rounded-lg bg-amber-500/15 text-amber-400"><Icon className="w-4 h-4" /></span>}
      </div>
      <div className="mt-3 text-3xl font-bold text-white">{value}</div>
    </div>
  );
}

export default function PlatformDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/platform/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading />;

  return (
    <div data-testid="platform-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">運営ダッシュボード</h1>
        <p className="text-sm text-slate-400 mt-1">プラットフォーム全体の売上・契約状況</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
        <Stat label="契約中の法人" value={d.active} icon={CheckCircle2} testid="stat-active" />
        <Stat label="登録法人数" value={d.companies} icon={Building2} testid="stat-companies" />
        <Stat label="MRR（月次）" value={yen(d.mrr)} icon={CircleDollarSign} testid="stat-mrr" />
        <Stat label="ARR（年次）" value={yen(d.arr)} icon={TrendingUp} testid="stat-arr" />
        <Stat label="解約率" value={`${d.churn}%`} icon={Percent} testid="stat-churn" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h3 className="font-semibold text-slate-200 mb-3">プラン別 契約数</h3>
          {Object.keys(d.by_plan || {}).length ? (
            <div className="space-y-2">
              {Object.entries(d.by_plan).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm" data-testid={`byplan-${k}`}>
                  <span className="text-slate-300">{k}</span>
                  <span className="font-semibold text-amber-400">{v}件</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500">契約中のプランはありません</p>}
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h3 className="font-semibold text-slate-200 mb-3">契約状態の内訳</h3>
          <div className="space-y-2">
            {Object.entries(d.status_breakdown || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm" data-testid={`status-${k}`}>
                <span className="text-slate-300">{k}</span>
                <span className="font-semibold text-slate-100">{v}社</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h3 className="font-semibold text-slate-200 mb-3">最近登録された法人</h3>
        <div className="divide-y divide-slate-800">
          {(d.recent || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`recent-${c.id}`}>
              <div>
                <div className="text-slate-200 font-medium">{c.name}</div>
                <div className="text-xs text-slate-500">{(c.created_at || "").slice(0, 10)}</div>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">{c.contract_status || "未契約"}</span>
            </div>
          ))}
          {!(d.recent || []).length && <p className="text-sm text-slate-500 py-4">法人がまだ登録されていません</p>}
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5 mt-8" data-testid="master-usage">
        <h3 className="font-semibold text-slate-200 mb-3">研修コンテンツ利用状況</h3>
        {(d.master_usage || []).length ? (
          <div className="space-y-4">
            {d.master_usage.map((m) => (
              <div key={m.id} data-testid={`usage-${m.id}`}>
                <div className="flex items-center justify-between text-sm gap-3">
                  <div className="min-w-0 truncate">
                    <span className="text-slate-200 font-medium">{m.name}</span>
                    {m.category && <span className="text-xs text-slate-500 ml-2">{m.category}</span>}
                  </div>
                  <div className="text-xs text-slate-400 shrink-0">導入 {m.adopted}法人 ・ 修了 {m.done}/{m.total}</div>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${m.rate}%` }} />
                </div>
                <div className="text-[11px] text-amber-400 mt-0.5">修了率 {m.rate}%</div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-500">まだ教材が利用されていません</p>}
      </div>
    </div>
  );
}
